import { getValidSaveSlotNumber, getSaveSlotDisplayProgramName } from './saveSlotProjectId';
import { slotNameFromSlotNumber } from './constants';

export type SlotStorageLike = Record<string, { programTitle?: string } | null | undefined> | null | undefined;

function normalizeExecutingPythonPath(name: string): string {
  return name.trim().replace(/\\/g, '/').replace(/\/+/g, '/').toLowerCase();
}

function executingPythonFilename(name: string): string {
  const trimmed = name.trim().replace(/\\/g, '/').replace(/\/+/g, '/');
  if (!trimmed) {
    return '';
  }

  const lastSlashIndex = trimmed.lastIndexOf('/');
  const filename = lastSlashIndex === -1 ? trimmed : trimmed.slice(lastSlashIndex + 1);
  return filename.toLowerCase() === 'empty.py' ? '' : filename;
}

/** User-facing slot number (1–8) from a drive-unit executing-python path or slot segment. */
export function slotNumberFromExecutingPythonPath(executingName: string | null | undefined): number | null {
  if (typeof executingName !== 'string' || !executingName.trim()) {
    return null;
  }

  const normalizedPath = normalizeExecutingPythonPath(executingName);
  const pathMatch = normalizedPath.match(/\/python_root\/(slot\d+)\//) ?? normalizedPath.match(/^(slot\d+)\//);
  const slotSegment = pathMatch?.[1];
  if (!slotSegment) {
    return null;
  }

  const slotNumber = Number(slotSegment.slice(4));
  const validSlotNumber = getValidSaveSlotNumber(slotNumber);
  if (validSlotNumber !== null) {
    return validSlotNumber;
  }

  // Firmware paths may use zero-based slot indices (slot0 → user slot 1).
  return getValidSaveSlotNumber(slotNumber + 1);
}

/** Human-friendly program label for snackbars from executing-python identity. */
export function resolveExecutingPythonDisplayName(
  executingName: string | null | undefined,
  slotStorage: SlotStorageLike,
  slotNumber: number | null = slotNumberFromExecutingPythonPath(executingName)
): string {
  if (slotNumber !== null) {
    const slotKey = slotNameFromSlotNumber(slotNumber);
    const programTitle = slotStorage?.[slotKey]?.programTitle;
    const displayName = getSaveSlotDisplayProgramName(programTitle);
    if (displayName) {
      return displayName;
    }
  }

  const filename = executingPythonFilename(executingName ?? '');
  if (!filename || filename === 'empty.py') {
    return '';
  }

  return getSaveSlotDisplayProgramName(filename);
}
